import Modal from 'components/Modal';
import { FormattedMessage } from '@alltrails/shared/react-intl';
import Logo from '@alltrails/shared/denali/components/Logo';
import Typography from '@alltrails/shared/denali/components/Typography';
import badgeCheckSrc from 'components/icons/badge-check.svg';
import { wrapUrlSafe } from 'utils/language_support_util';
import useLanguageRegionCode from '@alltrails/shared/hooks/useLanguageRegionCode';
import { Fragment, Key, ReactNode, useEffect } from 'react';
import CarouselDisplayTrigger from '@alltrails/analytics/enums/CarouselDisplayTrigger';
import PurchaseScreenTypePlan from '@alltrails/analytics/enums/PurchaseScreenTypePlan';
import logWebProModalPurchaseButtonTapped from '@alltrails/analytics/events/logWebProModalPurchaseButtonTapped';
import logWebProModalShown from '@alltrails/analytics/events/logWebProModalShown';
import WebProModalPurchaseButtonCopyText from '@alltrails/analytics/enums/WebProModalPurchaseButtonCopyText';
import useUser from 'hooks/useUser';
import * as styles from './UpsellModal.module.scss';

const features = [
  <FormattedMessage defaultMessage="Download offline maps" />,
  <FormattedMessage defaultMessage="Get alerts for wrong turns" />,
  <FormattedMessage defaultMessage="Find trails by distance from you" />
];

type Props = {
  heading: ReactNode;
  headerClassName: string;
  isOpen: boolean;
  onClick?: () => void;
  onRequestClose: () => void;
  returnTo: string;
  text: ReactNode;
  trigger: CarouselDisplayTrigger;
};

export default function UpsellModal({ headerClassName, isOpen, onClick, onRequestClose, returnTo, heading, text, trigger }: Props) {
  const languageRegionCode = useLanguageRegionCode();
  const user = useUser();

  const isPromoEligible = !user || user.isPromoEligible;

  useEffect(() => {
    logWebProModalShown({
      purchase_screen_type_plan: isPromoEligible ? PurchaseScreenTypePlan.FreeTrial : PurchaseScreenTypePlan.NonTrial,
      trigger
    });
  }, [isPromoEligible, trigger]);

  const formatter = new Intl.NumberFormat(languageRegionCode, {
    style: 'currency',
    currency: __AT_DATA__.currencyCode
  });

  const { plan } = __AT_DATA__;

  const params = new URLSearchParams();
  params.set('returnTo', returnTo);

  const href = wrapUrlSafe(`/plus?${params.toString()}`, languageRegionCode);

  return (
    <Modal
      background="cream"
      baseCtas={{
        finePrint: (
          <Typography variant="text100bold">
            {isPromoEligible ? (
              <FormattedMessage
                defaultMessage="Get 7 days free, then only {annualCost}/year (that’s {monthlyCost}/month). Cancel anytime."
                values={{
                  annualCost: formatter.format(Number(plan.priceTotal)),
                  monthlyCost: formatter.format(Number(plan.priceMonth))
                }}
              />
            ) : (
              <FormattedMessage
                defaultMessage="Only {annualCost}/year (that’s {monthlyCost}/month). Cancel anytime."
                values={{
                  annualCost: formatter.format(Number(plan.priceTotal)),
                  monthlyCost: formatter.format(Number(plan.priceMonth))
                }}
              />
            )}
          </Typography>
        ),
        orientation: 'vertical',
        primaryCta: {
          ...(!onClick && { linkInfo: { href } }),
          onClick: () => {
            logWebProModalPurchaseButtonTapped({
              copy_text: isPromoEligible
                ? WebProModalPurchaseButtonCopyText.StartYourFreeTrial
                : WebProModalPurchaseButtonCopyText.UpgradeToAllTrailsPlus,
              purchase_screen_type_plan: isPromoEligible ? PurchaseScreenTypePlan.FreeTrial : PurchaseScreenTypePlan.NonTrial,
              trigger
            });

            onClick?.();
          },
          text: isPromoEligible ? (
            <FormattedMessage defaultMessage="Start your free trial" />
          ) : (
            <FormattedMessage defaultMessage="Upgrade to AllTrails+" />
          ),
          testId: 'upsell-cta'
        }
      }}
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      size="lg"
      headerClassName={headerClassName}
      iconButtonVariant="default"
      closeButtonStyle={styles.close}
    >
      <div className={styles.content}>
        <div className={styles.title}>
          <Logo className={styles.logo} color="dark" size="sm" variant="plus-logomark" />
          <Typography component="div" variant="heading600">
            {heading}
          </Typography>
          <Typography component="div" variant="text300">
            {text}
          </Typography>
        </div>
        <div className={styles.features}>
          {features.map((feature, index) => (
            <Fragment key={index as Key}>
              <img alt="" src={badgeCheckSrc} />
              <Typography component="div" variant="text300">
                {feature}
              </Typography>
            </Fragment>
          ))}
        </div>
      </div>
    </Modal>
  );
}
